// shared/src/resolvers/crypto-coin-id.ts
// 2026-05-24 — translation lives in CODE, not in the prompt.
//
// The LLM (m.1.analyst, p.1.analyst-v1) naturally thinks in user-facing
// ticker forms — $BTC, BTC, "bitcoin" — when emitting request_tools
// entries for the crypto.* tools. The CoinGecko endpoints we hit under
// the hood require a specific `coin_id` (lowercase canonical slug:
// "bitcoin", "ethereum", "solana", …). Historically we attempted to
// teach the LLM the canonical slug in the prompt (m-analyst v2.2.0
// added "coin_id is the CoinGecko id NOT $BTC/$ETH"). That was the
// WRONG direction — every prompt rule we add is an artificial
// constraint that steals analyst cycles from analysis + reasoning.
//
// This resolver lets the LLM speak its natural language and the code
// figures out the lookup. Accepts ANY of:
//   · `$BTC` / `$ETH`           (canonical D1 form)
//   · `BTC`  / `ETH`            (bare ticker)
//   · `bitcoin` / `ethereum`    (CoinGecko slug)
// Resolves to the canonical CoinGecko coin id.
//
// Fail loud per [[feedback-no-fallbacks]]: unknown inputs return
// `null`. Caller throws a `ToolArgValidationError` with the rejected
// input + the list of accepted symbols (the WARN row that surfaces
// in /ops-lab → H2 stage-failure drill).
//
// Adding a new crypto ticker:
//   1. Add the bare ticker to shared/src/tickers.ts CRYPTO_TICKERS set
//   2. Add the BARE → coingecko slug mapping below
//   3. The forms `$<TICKER>` / `<TICKER>` / `<slug>` are then all
//      accepted automatically.

/**
 * BARE TICKER → CoinGecko canonical slug.
 *
 * Sourced from CoinGecko's coin list (https://api.coingecko.com/api/v3/coins/list).
 * Must cover every member of CRYPTO_TICKERS (shared/src/tickers.ts:23).
 *
 * Note (AZTEC, 2026-05-24 audit-r3 S-003): AZTEC has 3 CoinGecko candidates
 * (`aztec`, `aztec-network`, `azuro-protocol-aztec`). Pinned to `aztec`
 * (the L2 privacy protocol token) per user direction 2026-05-24 —
 * matches the ~$0.02 signal_price range in PROD signals table for
 * `$AZTEC` rows (LEGACY_BACKFILL catalyst, April 2026). Time-bomb
 * closed: any `crypto_ohlc(AZTEC)` call from m.1.analyst now resolves
 * to a valid CoinGecko slug instead of throwing ToolArgValidationError.
 */
const BARE_TO_COINGECKO: Readonly<Record<string, string>> = {
  BTC:   'bitcoin',
  ETH:   'ethereum',
  SOL:   'solana',
  XRP:   'ripple',
  DOGE:  'dogecoin',
  ADA:   'cardano',   // D44 (2026-05-28) — added with CRYPTO_TICKERS expansion
  AZTEC: 'aztec',     // L2 privacy protocol — pinned 2026-05-24 audit-r3 S-003
};

/**
 * Reverse map: CoinGecko slug → BARE ticker. Used only for the
 * "already-canonical input" passthrough check.
 */
const COINGECKO_TO_BARE: Readonly<Record<string, string>> = (() => {
  const out: Record<string, string> = {};
  for (const [bare, slug] of Object.entries(BARE_TO_COINGECKO)) {
    out[slug] = bare;
  }
  return out;
})();

/**
 * Resolve any natural ticker form to the CoinGecko canonical slug.
 *
 * Accepts: '$BTC' | 'BTC' | 'btc' | 'Bitcoin' | 'bitcoin' | …
 * Returns: 'bitcoin' (CoinGecko slug) for known cryptos.
 * Returns null for: unknown tickers, empty / non-string input.
 *
 * NEVER falls back to a "default" coin id — null surfaces the gap
 * to the caller, which throws a typed WARN per [[feedback-no-fallbacks]].
 */
export function resolveCoinGeckoId(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;

  // Step 1: slug-form passthrough — caller already gave us the canonical
  // CoinGecko slug. Lowercase + exact match against our reverse map.
  const lower = trimmed.toLowerCase();
  if (COINGECKO_TO_BARE[lower]) return lower;

  // Step 2: strip `$` + uppercase, look up bare → slug.
  const bare = trimmed.replace(/^\$/, '').toUpperCase();
  const slug = BARE_TO_COINGECKO[bare];
  return slug ?? null;
}

/**
 * List of accepted bare tickers (for surfacing in error messages so
 * the LLM gets a list of valid options on next retry).
 */
export function listKnownCryptoTickers(): ReadonlyArray<string> {
  return Object.freeze(Object.keys(BARE_TO_COINGECKO));
}
